import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { brandify } from "./Brand";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2, MessageCircle, Phone, Loader2 } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import { useLang } from "../lib/lang";
import { PROGRAMME_VALUES, QUALIFICATION_VALUES, PHONE, WA_DEFAULT, waLink } from "../lib/copy";
import { trackLead, trackWhatsApp, trackCall } from "../lib/analytics";
import { Reveal, Eyebrow, SectionTitle } from "./Reveal";

export const Faq = () => {
  const { t } = useLang();
  return (
    <section id="faq" className="scroll-mt-20 py-24 lg:py-32" data-testid="faq-section">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-[0.8fr_1.2fr]">
        <div>
          <Reveal><Eyebrow>{t.faq.eyebrow}</Eyebrow></Reveal>
          <Reveal delay={0.05}><SectionTitle>{t.faq.title}</SectionTitle></Reveal>
        </div>
        <Accordion type="single" collapsible className="space-y-3">
          {t.faq.items.map((f, i) => (
            <AccordionItem key={f.q} value={`q${i}`} className="rounded-2xl border border-ink/10 bg-white px-5 data-[state=open]:bg-sky/70 sm:px-7" data-testid={`faq-item-${i}`}>
              <AccordionTrigger className="py-5 text-left font-display text-base font-bold text-ink hover:no-underline sm:text-lg" data-testid={`faq-trigger-${i}`}>{f.q}</AccordionTrigger>
              <AccordionContent className="pb-5 text-base leading-relaxed text-night">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

const EMPTY = { name: "", mobile: "", email: "", city: "", qualification: "", programme: "", message: "", processing: false, contact: false, age: false, website: "" };

const validate = (v, e) => {
  const err = {};
  if (v.name.trim().length < 2) err.name = e.name;
  if (!/^[6-9]\d{9}$/.test(v.mobile.replace(/\D/g, "").replace(/^91(?=\d{10}$)/, ""))) err.mobile = e.mobile;
  if (v.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v.email.trim())) err.email = e.email;
  if (v.city.trim().length < 2) err.city = e.city;
  if (!v.qualification) err.qualification = e.qualification;
  if (!v.programme) err.programme = e.programme;
  if (!v.processing) err.processing = e.processing;
  if (!v.contact) err.contact = e.contact;
  if (!v.age) err.age = e.age;
  return err;
};

const inputCls = "mt-2 w-full rounded-xl border border-ink/25 bg-white px-4 py-3.5 text-base text-night placeholder:text-night/60 focus:border-ink focus:outline-none focus:ring-4 focus:ring-sun/40";

const Field = ({ label, error, id, children }) => (
  <div>
    <label htmlFor={id} className="font-display text-sm font-bold text-ink">{label}</label>
    {children}
    {error && <span id={`${id}-error`} className="mt-1.5 block text-sm font-medium text-red-700" role="alert" data-testid={`${id}-error`}>{error}</span>}
  </div>
);

const Check = ({ id, checked, onChange, error, children }) => (
  <div>
    <label htmlFor={id} className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-night">
      <input id={id} type="checkbox" checked={checked} onChange={onChange} aria-invalid={!!error} className="mt-0.5 h-5 w-5 shrink-0 accent-[#1E2B6B]" data-testid={id} />
      <span>{children}</span>
    </label>
    {error && <span className="ml-8 mt-1 block text-sm font-medium text-red-700" role="alert" data-testid={`${id}-error`}>{error}</span>}
  </div>
);

const Notice = ({ f }) => (
  <div className="rounded-2xl bg-sky/70 p-5 sm:col-span-2" data-testid="privacy-notice">
    <p className="font-display text-sm font-extrabold text-ink">{f.noticeTitle}</p>
    <dl className="mt-3 space-y-2 text-sm leading-relaxed text-night">
      {f.notice.map(([k, val]) => <div key={k}><dt className="inline font-bold">{k}: </dt><dd className="inline">{brandify(val)}</dd></div>)}
    </dl>
    <p className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-sm font-bold text-ink">
      <Link to="/privacy#withdraw" className="underline underline-offset-4" data-testid="notice-withdraw-link">{f.withdrawLink}</Link>
      <Link to="/privacy" className="underline underline-offset-4" data-testid="notice-privacy-link">{f.privacyLink}</Link>
    </p>
  </div>
);

const ThankYou = ({ f, values, onReset }) => {
  const text = `Hi Mirai Educations, I want free counselling.\nName: ${values.name}\nCity: ${values.city}\nProgramme: ${values.programme}`;
  return (
    <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="py-6" data-testid="enquiry-thank-you">
      <span className="flex h-16 w-16 items-center justify-center rounded-full bg-sun text-night"><CheckCircle2 className="h-8 w-8" aria-hidden="true" /></span>
      <h3 className="mt-6 font-display text-2xl font-extrabold text-ink sm:text-3xl">{f.thanksTitle}</h3>
      <p className="mt-3 text-base leading-relaxed text-night">{f.thanksBody}</p>
      <a href={waLink(text)} target="_blank" rel="noopener noreferrer" onClick={() => trackWhatsApp("thank-you")} data-testid="continue-whatsapp-button"
        className="mt-8 inline-flex items-center gap-2 rounded-full bg-ink px-7 py-4 font-display font-extrabold text-white hover:bg-night">
        <MessageCircle className="h-5 w-5" aria-hidden="true" />{f.continueWa}
      </a>
      <button onClick={onReset} className="mt-5 block font-display text-sm font-bold text-ink underline underline-offset-4" data-testid="enquiry-reset-button">{f.another}</button>
    </motion.div>
  );
};

export const Enquiry = ({ programme }) => {
  const { t, lang } = useLang();
  const f = t.form;
  const [v, setV] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [serverMsg, setServerMsg] = useState("");

  useEffect(() => {
    if (programme) setV((s) => ({ ...s, programme }));
  }, [programme]);

  const set = (k) => (e) => setV((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const err = validate(v, f.errors);
    setErrors(err);
    if (Object.keys(err).length) return;
    setStatus("sending");
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, {
        name: v.name, mobile: v.mobile, email: v.email.trim() || null, city: v.city, qualification: v.qualification,
        programme: v.programme, message: v.message || null, lang, website: v.website || null,
        consent: { processing: v.processing, contact: v.contact, age_confirmed: v.age },
      });
      trackLead(v.programme);
      setStatus("done");
    } catch (ex) {
      setServerMsg(typeof ex.response?.data?.detail === "string" ? ex.response.data.detail : f.errors.server);
      setStatus("error");
    }
  };

  return (
    <section id="contact" className="scroll-mt-20 bg-white py-24 lg:py-32" data-testid="enquiry-section">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-[0.8fr_1.2fr]">
        <div>
          <Reveal><Eyebrow>{f.eyebrow}</Eyebrow></Reveal>
          <Reveal delay={0.05}><SectionTitle>{f.title}</SectionTitle></Reveal>
          <p className="mt-6 max-w-md text-base leading-relaxed text-night sm:text-lg">{f.body}</p>
          <p className="mt-10 font-display text-xs font-bold uppercase tracking-[0.2em] text-night">{f.phoneLabel}</p>
          <a href={`tel:+91${PHONE}`} onClick={() => trackCall("enquiry")} className="mt-2 flex items-center gap-3 font-display text-3xl font-extrabold text-ink sm:text-4xl" data-testid="enquiry-phone-link"><Phone className="h-7 w-7" aria-hidden="true" />{PHONE}</a>
          <a href={WA_DEFAULT} target="_blank" rel="noopener noreferrer" onClick={() => trackWhatsApp("enquiry-side")} className="mt-3 inline-flex items-center gap-2 font-display font-bold text-ink underline underline-offset-8" data-testid="enquiry-whatsapp-link"><MessageCircle className="h-4 w-4" aria-hidden="true" />{t.float}</a>
        </div>
        <div className="rounded-[2rem] border border-ink/10 bg-mist p-6 sm:p-10">
          {status === "done" ? (
            <ThankYou f={f} values={v} onReset={() => { setV(EMPTY); setStatus("idle"); }} />
          ) : (
            <form onSubmit={submit} noValidate className="grid gap-5 sm:grid-cols-2" data-testid="enquiry-form">
              <Field id="f-name" label={f.name} error={errors.name}><input id="f-name" className={inputCls} value={v.name} onChange={set("name")} autoComplete="name" maxLength={80} aria-invalid={!!errors.name} data-testid="enquiry-form-name-input" /></Field>
              <Field id="f-mobile" label={f.mobile} error={errors.mobile}><input id="f-mobile" className={inputCls} value={v.mobile} onChange={set("mobile")} inputMode="numeric" autoComplete="tel" maxLength={14} aria-invalid={!!errors.mobile} data-testid="enquiry-form-mobile-input" /></Field>
              <Field id="f-email" label={f.email} error={errors.email}><input id="f-email" type="email" className={inputCls} value={v.email} onChange={set("email")} autoComplete="email" maxLength={120} data-testid="enquiry-form-email-input" /></Field>
              <Field id="f-city" label={f.city} error={errors.city}><input id="f-city" className={inputCls} value={v.city} onChange={set("city")} autoComplete="address-level2" maxLength={60} aria-invalid={!!errors.city} data-testid="enquiry-form-city-input" /></Field>
              <Field id="f-qual" label={f.qualification} error={errors.qualification}>
                <select id="f-qual" className={inputCls} value={v.qualification} onChange={set("qualification")} data-testid="enquiry-form-qualification-select">
                  <option value="">{f.select}</option>
                  {QUALIFICATION_VALUES.map((q, i) => <option key={q} value={q}>{f.qualifications[i]}</option>)}
                </select>
              </Field>
              <Field id="f-prog" label={f.programme} error={errors.programme}>
                <select id="f-prog" className={inputCls} value={v.programme} onChange={set("programme")} data-testid="enquiry-form-program-select">
                  <option value="">{f.select}</option>
                  {PROGRAMME_VALUES.map((p, i) => <option key={p} value={p}>{f.programmes[i]}</option>)}
                </select>
              </Field>
              <div className="sm:col-span-2">
                <Field id="f-msg" label={f.message}><textarea id="f-msg" rows={3} maxLength={1000} className={inputCls} value={v.message} onChange={set("message")} data-testid="enquiry-form-message-input" /></Field>
              </div>
              <div className="hidden" aria-hidden="true"><label htmlFor="f-website">Website</label><input id="f-website" tabIndex={-1} autoComplete="off" value={v.website} onChange={set("website")} /></div>
              <Notice f={f} />
              <div className="space-y-4 sm:col-span-2">
                <Check id="consent-processing-checkbox" checked={v.processing} onChange={set("processing")} error={errors.processing}>{brandify(f.processing)}</Check>
                <Check id="consent-contact-checkbox" checked={v.contact} onChange={set("contact")} error={errors.contact}>{f.contact}</Check>
                <Check id="consent-age-checkbox" checked={v.age} onChange={set("age")} error={errors.age}>{f.age}</Check>
              </div>
              {status === "error" && <p className="text-sm font-medium text-red-700 sm:col-span-2" role="alert" data-testid="enquiry-form-error">{serverMsg}</p>}
              <button type="submit" disabled={status === "sending"} data-testid="enquiry-form-submit-button"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-ink px-8 py-4 font-display text-base font-extrabold text-white hover:bg-night disabled:opacity-70 sm:col-span-2">
                {status === "sending" ? <><Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />{f.sending}</> : f.submit}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
